import React, { useMemo } from 'react';

import { AskAiRule } from '@lumapps/ask-ai-rules/types';
import { AskAiAnswer } from '@lumapps/ask-ai/components/AskAiAnswer';
import { AskAiAnswerFooter } from '@lumapps/ask-ai/components/AskAiAnswerFooter';
import { AskAiAuthorizationErrorsList } from '@lumapps/ask-ai/components/AskAiAuthorizationErrorsList';
import { AskSearchBestAnswer } from '@lumapps/ask-ai/types';
import { askAnswerHasAuthError } from '@lumapps/ask-ai/utils';
import { useClassnames } from '@lumapps/classnames';
import { ChatMessage } from '@lumapps/lumx-chat/components/ChatMessage';
import { ChatMessageGroup } from '@lumapps/lumx-chat/components/ChatMessageGroup';
import { ChatMessageLoading } from '@lumapps/lumx-chat/components/ChatMessageLoading';
import { ColorPalette, ColorVariant, Text, Typography } from '@lumapps/lumx/react';
import { useTranslate } from '@lumapps/translations';

import { ASK_AI_CONVERSATION } from '../../keys';
import type { AskAiChatMessage } from '../../types';
import { groupAskAiChatQuestionWithAnswer } from '../../utils';
import { AskAiSuggestedQuestions } from '../AskAiSuggestedQuestions';

import './index.scss';

export interface AskAiChatMessagesThreadProps {
    /** Messages to display */
    messages: AskAiChatMessage[];
    /** Callback to refetch */
    refetch: () => void;
    /** Callback when a suggested question is selected */
    onSelectSuggestedQuestion?: (question: string) => void;
    /** Whether a new message is currently being streamed */
    isStreaming?: boolean;
    /** When streaming is disabled  */
    isLoading?: boolean;
    /** Full answer given by Ask AI */
    bestAnswer?: AskSearchBestAnswer;
    /** Should we display the agent name or not */
    shouldDisplayAgentName?: boolean;
    /** Handle change agentId - receives the new rule and the message ID */
    handleAgentChange?: (rule: AskAiRule, messageId: string) => void;
}

const CLASSNAME = 'ask-ai-chat-message-thread';

/** Display the ask ai chat messages */
export const AskAiChatMessagesThread: React.FC<AskAiChatMessagesThreadProps> = ({
    messages = [],
    refetch,
    isStreaming,
    bestAnswer,
    isLoading,
    onSelectSuggestedQuestion,
    shouldDisplayAgentName = false,
    handleAgentChange,
}) => {
    const { element } = useClassnames(CLASSNAME);
    const { translateKey } = useTranslate();

    const groups = useMemo(() => groupAskAiChatQuestionWithAnswer(messages), [messages]);
    return (
        <>
            {groups.map((group, groupIndex) => {
                const isLastGroup = groupIndex === groups.length - 1;
                const shouldScrollIntoView = (): boolean | ScrollIntoViewOptions => {
                    if (groupIndex === 0 && isLastGroup && !isStreaming) {
                        return { block: 'center' };
                    }
                    return Boolean(groupIndex > 0 && isLastGroup && !isStreaming);
                };

                return (
                    <ChatMessageGroup
                        className={element('group')}
                        key={group.id}
                        /**
                         * Only scroll the last message into view if not streaming
                         */
                        scrollIntoView={shouldScrollIntoView()}
                    >
                        {group.messages.map((message, messageIndex) => {
                            const isUserMessage = message.role === 'user';
                            const hasAuthErrors = message.role === 'agent' && askAnswerHasAuthError(message.content);
                            return (
                                <React.Fragment key={message.id}>
                                    <ChatMessage
                                        className={element('message', {
                                            [message.role]: true,
                                        })}
                                        type={message.role === 'user' ? 'sent' : 'received'}
                                    >
                                        {isUserMessage ? (
                                            <Text as="p">{message.content}</Text>
                                        ) : (
                                            <AskAiAnswer
                                                traceId={group.messages[messageIndex - 1]?.id}
                                                answer={message.content}
                                                query={message.content.query || ''}
                                                shouldDisplayAgentName={shouldDisplayAgentName}
                                                shouldDisplayFooter={false}
                                            />
                                        )}
                                    </ChatMessage>
                                    {!isUserMessage && (
                                        <>
                                            {message.manuallyCancelled && (
                                                <Text
                                                    as="p"
                                                    color={ColorPalette.dark}
                                                    colorVariant={ColorVariant.L3}
                                                    typography={Typography.caption}
                                                >
                                                    {translateKey(ASK_AI_CONVERSATION.YOU_STOPPED_THIS_RESPONSE)}
                                                </Text>
                                            )}
                                            <AskAiAnswerFooter
                                                answer={message.content}
                                                traceId={message.id}
                                                isAnimated
                                                shouldDisplayAgentName
                                                handleAgentChange={handleAgentChange}
                                            />
                                            {hasAuthErrors && (
                                                <ChatMessage type="received">
                                                    <AskAiAuthorizationErrorsList
                                                        authErrors={message.content.authErrors}
                                                        refetch={refetch}
                                                    />
                                                </ChatMessage>
                                            )}
                                            {isLastGroup &&
                                                message.content.suggestedFollowUpQuestions &&
                                                onSelectSuggestedQuestion &&
                                                // Only show suggested questions if not currently streaming a new answer
                                                !isStreaming && (
                                                    <AskAiSuggestedQuestions
                                                        suggestionQuestions={message.content.suggestedFollowUpQuestions}
                                                        onSelect={onSelectSuggestedQuestion}
                                                    />
                                                )}
                                        </>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </ChatMessageGroup>
                );
            })}
            {(isStreaming || isLoading) && (
                <ChatMessageGroup className={element('group')} scrollIntoView={false}>
                    {bestAnswer?.answer ? (
                        <ChatMessage
                            className={element('message', {
                                received: true,
                                streaming: true,
                            })}
                            type="received"
                        >
                            <AskAiAnswer
                                traceId=""
                                answer={{ answer: bestAnswer.answer }}
                                query=""
                                isStreaming
                                shouldDisplayAgentName={shouldDisplayAgentName}
                                shouldDisplayFooter
                            />
                        </ChatMessage>
                    ) : (
                        <ChatMessageLoading />
                    )}
                </ChatMessageGroup>
            )}
        </>
    );
};
